import type { QueryClient } from "@tanstack/react-query";
import { globalKey } from "../../../app/query/queryKeys";
import { refreshFinanceData } from "../../../app/operations/financeRefresh";

const QUERY_AREA = {
  person: "person", persons: "persons", checkIns: "check-ins", paymentProfile: "person-payment-profile",
  settingsList: "settings-list", settingsListRows: "settings-list-rows", settingsListScopeWiden: "settings-list-scope-widen"
} as const;
const CHECK_INS_ACTIVE_KEY = "active";

export const PERSON_DETAIL_LOOKUP_STALE_TIME_MS = 5 * 60 * 1000;
export const PERSON_DETAIL_PAYMENTS_STALE_TIME_MS = Infinity;
export const PERSON_DETAIL_PAYMENTS_GC_TIME_MS = 0;

export const personDetailQueryKeys = {
  person: {
    detail: (personId: string) => ["person", personId, "detail"] as const,
    activeCheckIn: (personId: string) => ["person", personId, "active-check-in"] as const,
    checkInEligibility: (personId: string, studioIdsParam: string) =>
      ["person", personId, "check-in-eligibility", studioIdsParam] as const,
    overview: (personId: string) => globalKey("persons", personId, "overview"),
    contractPackages: (personId: string) => ["person", personId, "contract-packages"] as const,
    contractBlocks: (personId: string) => ["person", personId, "contract-blocks"] as const,
    contractTargets: (personId: string) => ["person", personId, "contract-targets"] as const,
    contributions: (personId: string) => ["person", personId, "contributions"] as const,
    openItems: (personId: string) => ["person", personId, "open-items"] as const,
    payments: (personId: string) => ["person", personId, "payments"] as const,
    wallets: (personId: string) => ["person", personId, "wallets"] as const,
    documents: (personId: string) => ["person", personId, "documents"] as const,
    priceAdjustments: (personId: string) => ["person", personId, "price-adjustments"] as const
  },
  lookups: {
    contractTemplates: ["lookups", "contract-package-templates"] as const,
    contractTemplateBlocks: ["lookups", "contract-template-blocks"] as const,
    contractOptionGroups: ["lookups", "contract-option-groups"] as const,
    paymentMethods: ["lookups", "payment-methods"] as const,
    paymentPlans: ["lookups", "payment-plans"] as const,
    priceAdjustments: ["lookups", "price-adjustments"] as const,
    restTemplates: ["lookups", "rest-templates"] as const,
    cancellationModels: ["lookups", "cancellation-models"] as const,
    accounts: ["lookups", "accounts"] as const,
    sepaGroups: ["lookups", "sepa-groups"] as const,
    walletProducts: ["lookups", "wallet-products"] as const
  }
};

/**
 * Zutrittszustand neu bewerten lassen. Ohne Studio-Parameter, damit alle Studio-Varianten
 * im Cache erfasst werden.
 */
export function invalidatePersonCheckInEligibility(queryClient: QueryClient, personId: string) {
  void queryClient.invalidateQueries({ queryKey: ["person", personId, "check-in-eligibility"] });
}

/** Auch die eigenständigen Listen-Caches müssen neu laden, nicht nur ihre inneren fetchQuery-Einträge. */
export function invalidatePersonDetail(queryClient: QueryClient, personId: string) {
  return queryClient.invalidateQueries({
    predicate: ({ queryKey }) => {
      const [area, key] = queryKey;
      if ((area === QUERY_AREA.person || area === QUERY_AREA.persons) && key === personId) return true;
      if (area === QUERY_AREA.checkIns && key === CHECK_INS_ACTIVE_KEY && queryKey[2] === personId) return true;
      if (area === QUERY_AREA.paymentProfile && queryKey.at(-1) === personId) return true;
      // Die Reiter verwenden eigene SettingsListPage-Schlüssel. Nur Aktenlisten erreichen;
      // andere Listen, Filter, Sortierung und die aktuelle Seite bleiben erhalten.
      return (area === QUERY_AREA.settingsList || area === QUERY_AREA.settingsListRows || area === QUERY_AREA.settingsListScopeWiden)
        && typeof key === "string" && key.startsWith("persons.detail.");
    },
  });
}

export function invalidatePersonContractData(queryClient: QueryClient, personId: string) {
  return refreshFinanceData(queryClient, personId, { throwOnError: false });
}

export function invalidatePersonFinanceData(queryClient: QueryClient, personId: string) {
  return refreshFinanceData(queryClient, personId, { throwOnError: false });
}
